import { Box, Button, Card, CardBody, Flex, Stack } from "@chakra-ui/react";
import CDS from "design-system";
import { FiEye } from "react-icons/fi";
import { Link as RouterLink, useParams } from "react-router";
import AppPage from "layouts/AppPage";
import { useEffect } from "react";
import { useNavbar } from "hooks/useNavbar";
import { useFeature } from "hooks/useFeature";
import QueryError from "components/Error/QueryError";
import { useV2PathDetailQuery, usePathBatchCountsQuery } from "features/Path/hooks";
import { Heading16 } from "components/Heading";
import PathMenuButton from "features/Path/Components/Button/PathMenuButton";
import ListTemplatesByPathId from "features/Resources/SharedTemplate/Components/ListTemplatesByPathId";
import AddTemplateToPathButton from "features/Path/Components/Button/AddTemplateToPathButton";
import CaminoBreadcrumbs from "components/Breadcrumbs";
import EditablePathHeader from "features/Path/Components/PathsList/EditablePathHeader";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import { truncateEnd } from "helpers/string";
import { JOURNEY_PREVIEWER_FEATURE_FLAG } from "features/JourneyPreview";

export default function PathPage() {
  const { id } = useParams<{ id: string }>();
  const { setPageTitle } = useNavbar();
  const { enabled: journeyPreviewerEnabled } = useFeature(JOURNEY_PREVIEWER_FEATURE_FLAG);

  const { data, isLoading, error: queryError } = useV2PathDetailQuery(id || "");
  const { data: detailsMap = {} } = usePathBatchCountsQuery(id ? [id] : []);
  const pathWithCounts = data && id ? { ...data, ...(detailsMap[id] ?? {}) } : data;

  useEffect(() => {
    if (data && !isLoading) {
      setPageTitle(data?.name);
    }
  }, [data, isLoading, setPageTitle]);

  if (data && queryError) {
    return <QueryError queryError={queryError} id={id} resourceName="Path" />;
  }

  return (
    <AppPage>
      <CaminoBreadcrumbs
        breadcrumbInfos={[{ name: "Paths", toUrl: SHARED_ROUTES.paths.index }, { name: `${truncateEnd(data?.name)}` }]}
      />
      <Card mt="8">
        <CardBody>
          <Stack spacing="6">
            <Flex justify="space-between" align="flex-start" gap="4">
              {pathWithCounts ? <EditablePathHeader path={pathWithCounts} /> : <Box />}

              {data && (
                <Flex gap="2" flexShrink={0}>
                  {journeyPreviewerEnabled && (
                    <Button
                      as={RouterLink}
                      to={`${SHARED_ROUTES.paths.preview}?pathIds=${data.id}`}
                      size="sm"
                      variant="outline"
                      leftIcon={<FiEye />}
                      height="32px"
                    >
                      Preview journey
                    </Button>
                  )}
                  <AddTemplateToPathButton menuButtonProps={{ height: "32px" }} pathId={data?.id} />
                  <PathMenuButton path={data} />
                </Flex>
              )}
            </Flex>

            <Box>
              <Flex align="baseline" justify="space-between" mb="3">
                <Heading16>Schedule</Heading16>
                {data && (
                  <CDS.Text12 color="gray.500">
                    {pathWithCounts?.stepsCount}
                    {" "}
                    {pathWithCounts?.stepsCount === 1 ? "item" : "items"}
                  </CDS.Text12>
                )}
              </Flex>
              {id && <ListTemplatesByPathId pathId={id} />}
            </Box>
          </Stack>
        </CardBody>
      </Card>
    </AppPage>
  );
}
